import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ClientType } from '@app/core/state/types';
import { CLIENT_TYPE_SOLE_PROPRIETORSHIP } from '@app/shared/misc/constants';
import { TranslocoModule } from '@jsverse/transloco';
import { AutoFocusModule } from 'primeng/autofocus';
import { CheckboxModule } from 'primeng/checkbox';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { FocusTrapModule } from 'primeng/focustrap';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableFormDialogBaseComponent } from 'src/app/core/i18n/translatable-form-dialog-base.component';
import { DialogData, DialogForm, DialogResult } from '../../form-dialog-base/form-dialog-base.component';
import { BlFrontendButtonComponent } from 'src/app/core/wrappers/bl-frontend-button.component';
import { SafeHtmlPipe } from 'src/app/shared/pipes/safe-html.pipe';

interface Data extends DialogData {
  client: Partial<ClientType>;
}

interface Result extends DialogResult {
  client: Partial<ClientType>;
}

interface Form extends DialogForm<unknown> {
  nameChecked: FormControl<boolean>;
  addressChecked: FormControl<boolean>;
  phoneChecked: FormControl<boolean>;
  emailChecked: FormControl<boolean>;
  webpageChecked: FormControl<boolean>;
  residenceChecked: FormControl<boolean>;
  businessAreaChecked: FormControl<boolean>;
  registrationDateChecked: FormControl<boolean>;
  accountantChecked: FormControl<boolean>;
  descriptionChecked: FormControl<boolean>;
  signatoryChecked: FormControl<boolean>;
}

const TRANSLATION_SCOPE = 'creditInformationUpdateDialog';

@Component({
  selector: 'app-credit-information-update-dialog',
  template: `
    <ng-container *transloco="let t; prefix: scope">
      <form [formGroup]="form" (ngSubmit)="onSubmit()" pFocusTrap>
        <p
          class="font-semibold"
          [innerHTML]="
            t('collectingBasicInfoFor', { corporateIdentity: config.data.client.corporateIdentity }) | safeHtml
          "></p>
        <p>{{ t('selectedInfoWillWriteOverExisting') }}</p>
        <p class="font-semibold mt-5">{{ t('registeredInfo') }}</p>
        <div class="mb-2 flex items-start">
          <p-checkbox
            name="nameChecked"
            [binary]="true"
            formControlName="nameChecked"
            pAutoFocus
            [autofocus]="true"></p-checkbox>
          <label class="ml-2" for="nameChecked"
            ><strong>{{ t('name') }} </strong>{{ config.data.client.name }}</label
          >
        </div>
        <div class="mb-2 flex items-start">
          <p-checkbox name="addressChecked" [binary]="true" formControlName="addressChecked"></p-checkbox>
          <label class="ml-2" for="addressChecked"
            ><strong>{{ t('address') }} </strong>{{ config.data.client.address }}, {{ config.data.client.zipCode }}
            {{ config.data.client.city }}</label
          >
        </div>
        <div class="mb-2 flex items-start">
          <p-checkbox name="phoneChecked" [binary]="true" formControlName="phoneChecked"></p-checkbox>
          <label class="ml-2" for="phoneChecked"
            ><strong>{{ t('phone') }} </strong>{{ config.data.client.phone }}</label
          >
        </div>
        <div class="mb-2 flex items-start">
          <p-checkbox name="emailChecked" [binary]="true" formControlName="emailChecked"></p-checkbox>
          <label class="ml-2" for="emailChecked"
            ><strong>{{ t('email') }} </strong>{{ config.data.client.email }}</label
          >
        </div>
        <div class="mb-2 flex items-start">
          <p-checkbox name="webpageChecked" [binary]="true" formControlName="webpageChecked"></p-checkbox>
          <label class="ml-2" for="webpageChecked"
            ><strong>{{ t('webpage') }} </strong>{{ config.data.client.webpage }}</label
          >
        </div>
        <div class="mb-2 flex items-start">
          <p-checkbox name="residenceChecked" [binary]="true" formControlName="residenceChecked"></p-checkbox>
          <label class="ml-2" for="residenceChecked"
            ><strong>{{ t('residence') }} </strong>{{ config.data.client.residence }}</label
          >
        </div>
        <div class="mb-2 flex items-start">
          <p-checkbox name="businessAreaChecked" [binary]="true" formControlName="businessAreaChecked"></p-checkbox>
          <label class="ml-2" for="businessAreaChecked"
            ><strong>{{ t('businessArea') }} </strong>{{ config.data.client.businessArea }}</label
          >
        </div>
        <div class="mb-2 flex items-start">
          <p-checkbox
            name="registrationDateChecked"
            [binary]="true"
            formControlName="registrationDateChecked"></p-checkbox>
          <label class="ml-2" for="registrationDateChecked"
            ><strong>{{ t('registrationDate') }} </strong>{{ config.data.client.registrationDate }}</label
          >
        </div>
        <div class="mb-2 flex items-start">
          <p-checkbox name="accountantChecked" [binary]="true" formControlName="accountantChecked"></p-checkbox>
          <label class="ml-2" for="accountantChecked"
            ><strong>{{ t('accountant') }} </strong>{{ config.data.client.accountant }}</label
          >
        </div>
        <div class="mb-2 flex items-start">
          <p-checkbox name="descriptionChecked" [binary]="true" formControlName="descriptionChecked"></p-checkbox>
          <label class="ml-2" for="descriptionChecked"
            ><strong>{{ t('businessDescription') }} </strong>{{ config.data.client.description }}</label
          >
        </div>
        <div class="mb-2 flex items-start" *ngIf="isSoleProprietorship">
          <p-checkbox name="signatoryChecked" [binary]="true" formControlName="signatoryChecked"></p-checkbox>
          <label class="ml-2" for="signatoryChecked"
            ><strong>{{ t('signatory') }} </strong>{{ config.data.client.signatory }}</label
          >
        </div>
        <div class="flex flex-row w-full justify-end mt-4">
          <bl-frontend-button
            variant="secondary"
            class="mr-2"
            (onclick)="close({ client: null })"
            [text]="'shared.cancel' | transloco"></bl-frontend-button>
          <bl-frontend-button type="submit" [text]="'shared.update' | transloco"></bl-frontend-button>
        </div>
      </form>
    </ng-container>
  `,
  standalone: true,
  providers: [getTranslationProvider(TRANSLATION_SCOPE)],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CheckboxModule,
    FocusTrapModule,
    AutoFocusModule,
    TranslocoModule,
    BlFrontendButtonComponent,
    SafeHtmlPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreditInformationUpdateDialog extends TranslatableFormDialogBaseComponent<Data, Result, Form> {
  get isSoleProprietorship(): boolean {
    return this.config.data.client.type?.name === CLIENT_TYPE_SOLE_PROPRIETORSHIP;
  }

  onSubmit() {
    const name = this.getCheckedValue('nameChecked', 'name');
    const address = this.getCheckedValue('addressChecked', 'address');
    const zipCode = this.getCheckedValue('addressChecked', 'zipCode');
    const city = this.getCheckedValue('addressChecked', 'city');
    const phone = this.getCheckedValue('phoneChecked', 'phone');
    const email = this.getCheckedValue('emailChecked', 'email');
    const webpage = this.getCheckedValue('webpageChecked', 'webpage');
    const residence = this.getCheckedValue('residenceChecked', 'residence');
    const businessArea = this.getCheckedValue('businessAreaChecked', 'businessArea');
    const registrationDate = this.getCheckedValue('registrationDateChecked', 'registrationDate');
    const accountant = this.getCheckedValue('accountantChecked', 'accountant');
    const description = this.getCheckedValue('descriptionChecked', 'description');
    const signatory = this.getCheckedValue('signatoryChecked', 'signatory');

    const client: Partial<ClientType> = {
      name,
      address,
      zipCode,
      city,
      phone,
      email,
      webpage,
      residence,
      businessArea,
      registrationDate,
      accountant,
      description,
      signatory,
    };

    this.close({ client: this.cleanUpUnusedProps(client) });
  }

  protected override onInitialized = () => {
    const {
      name,
      address,
      phone,
      email,
      webpage,
      residence,
      businessArea,
      registrationDate,
      accountant,
      description,
      signatory,
    } = this.config.data.client;
    this.form = this.builder.group({
      nameChecked: new FormControl(Boolean(name)),
      addressChecked: new FormControl(Boolean(address)),
      phoneChecked: new FormControl(Boolean(phone)),
      emailChecked: new FormControl(Boolean(email)),
      webpageChecked: new FormControl(Boolean(webpage)),
      residenceChecked: new FormControl(Boolean(residence)),
      businessAreaChecked: new FormControl(Boolean(businessArea)),
      registrationDateChecked: new FormControl(Boolean(registrationDate)),
      accountantChecked: new FormControl(Boolean(accountant)),
      descriptionChecked: new FormControl(Boolean(description)),
      signatoryChecked: new FormControl(this.isSoleProprietorship ? Boolean(signatory) : false),
    });
  };

  protected static override getTranslationScope(): string {
    return TRANSLATION_SCOPE;
  }

  protected static override getDialogConfig(
    translation: Record<string, string>,
  ): Omit<DynamicDialogConfig<Data>, 'data'> {
    return {
      header: translation.updateClientInfo,
      width: '500px',
      draggable: true,
      closable: true,
      modal: true,
      resizable: false,
    };
  }

  private getCheckedValue = (checkedKey: string, clientKey: string) =>
    this.form.value[checkedKey] ? this.config.data.client[clientKey] : null;

  private cleanUpUnusedProps(client: Partial<ClientType>): Partial<ClientType> {
    return Object.entries(client).reduce((acc, [key, value]) => {
      if (!value) {
        return acc;
      }

      acc[key] = value;
      return acc;
    }, {} as Partial<ClientType>);
  }
}
